import React, { useState, useEffect } from 'react';
import {
  Typography, TextField, Paper, Switch, FormControlLabel, Button, Stack, Snackbar, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, MenuItem, Select, InputLabel, FormControl
} from '@mui/material';
import axios from '../../utils/axios';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const colorOptions = [
  { value: 'info', label: 'Xanh dương (Info)' },
  { value: 'success', label: 'Xanh lá (Success)' },
  { value: 'warning', label: 'Vàng (Warning)' },
  { value: 'error', label: 'Đỏ (Error)' },
];

// Helper để chuyển mã #rgb sang #rrggbb
const toFullHex = (color) => {
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return '#' + color.slice(1).split('').map(x => x + x).join('');
  }
  return color;
};

export default function Notifications() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [color, setColor] = useState('info');
  const [textColor, setTextColor] = useState('#ffffff');
  const [isActive, setIsActive] = useState(true);
  const [notiId, setNotiId] = useState(null);
  const [list, setList] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: "", error: false });

  const fetchNotis = () => {
    axios.get('/notifications')
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : res.data.notifications;
        // Sắp xếp theo order tăng dần (nếu có order)
        arr?.sort?.((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setList(arr || []);
      });
  };

  useEffect(() => {
    fetchNotis();
  }, []);

  const resetForm = () => {
    setNotiId(null);
    setTitle('');
    setContent('');
    setUrl('');
    setColor('info');
    setTextColor('#ffffff');
    setIsActive(true);
  };

  const handleSubmit = () => {
    if (!title)
      return setSnack({ open: true, msg: "Vui lòng nhập tiêu đề", error: true });

    // Đảm bảo textColor luôn là #rrggbb
    const safeColor = toFullHex(textColor);

    const data = { title, content, url, color, textColor: safeColor, is_active: isActive };

    if (notiId) {
      axios.put(`/notifications/${notiId}`, data)
        .then(() => {
          setSnack({ open: true, msg: "Đã cập nhật thành công", error: false });
          resetForm();
          fetchNotis();
        }).catch((err) => {
          setSnack({ open: true, msg: err?.response?.data?.message || "Cập nhật thất bại", error: true });
        });
    } else {
      axios.post('/notifications', data)
        .then(() => {
          setSnack({ open: true, msg: "Đã tạo thông báo thành công", error: false });
          resetForm();
          fetchNotis();
        }).catch((err) => {
          setSnack({ open: true, msg: err?.response?.data?.message || "Tạo thông báo thất bại", error: true });
        });
    }
  };

  const handleEdit = n => {
    setNotiId(n.id);
    setTitle(n.title || "");
    setContent(n.content || "");
    setUrl(n.url || "");
    setColor(n.color || 'info');
    setTextColor(toFullHex(n.textColor || '#ffffff'));
    setIsActive(!!n.is_active);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = id => {
    if (window.confirm('Bạn có chắc muốn xoá?')) {
      axios.delete(`/notifications/${id}`)
        .then(() => {
          setSnack({ open: true, msg: "Đã xoá thành công", error: false });
          fetchNotis();
        }).catch(() => {
          setSnack({ open: true, msg: "Xoá thất bại", error: true });
        });
    }
  };

  // Đổi thứ tự thông báo
  const handleMove = (id, order, direction) => {
    const newOrder = order + direction;
    axios.patch(`/notifications/${id}/order`, { newOrder })
      .then(() => fetchNotis())
      .catch(() => setSnack({ open: true, msg: "Không thể đổi thứ tự!", error: true }));
  };

  return (
    <Paper sx={{ p: 4, borderRadius: 4, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {notiId ? "Cập nhật thông báo" : "Tạo thông báo mới"}
      </Typography>
      <Stack spacing={3}>
        <TextField label="Tiêu đề" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
        <TextField label="Nội dung (tùy chọn)" value={content} onChange={e => setContent(e.target.value)} fullWidth multiline rows={3} />
        <TextField label="URL (tuỳ chọn)" value={url} onChange={e => setUrl(e.target.value)} fullWidth />
        <FormControl fullWidth>
          <InputLabel>Chọn màu thông báo</InputLabel>
          <Select
            value={color}
            label="Chọn màu thông báo"
            onChange={e => setColor(e.target.value)}
          >
            {colorOptions.map(opt => (
              <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Chọn màu chữ */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Màu chữ</Typography>
          <input
            type="color"
            value={toFullHex(textColor)}
            onChange={e => setTextColor(toFullHex(e.target.value))}
            style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer' }}
          />
        </Stack>
        <FormControlLabel
          control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
          label="Hiển thị thông báo"
        />
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleSubmit}>
            {notiId ? "Lưu cập nhật" : "Tạo thông báo"}
          </Button>
          {notiId && <Button variant="outlined" color="secondary" onClick={resetForm}>Huỷ</Button>}
        </Stack>
      </Stack>

      <Typography variant="h6" mt={6} mb={2} fontWeight={700}>
        Danh sách thông báo đã tạo
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Thứ tự</TableCell>
            <TableCell>Tiêu đề</TableCell>
            <TableCell>Nội dung</TableCell>
            <TableCell>URL</TableCell>
            <TableCell>Màu</TableCell>
            <TableCell>Màu chữ</TableCell>
            <TableCell>Hiển thị</TableCell>
            <TableCell align="center">Thao tác</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((n, idx) => (
            <TableRow key={n.id}>
              <TableCell>
                {/* Nút ↑ ↓ đổi thứ tự */}
                <IconButton
                  size="small"
                  onClick={() => handleMove(n.id, n.order, -1)}
                  disabled={idx === 0}
                  title="Lên"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMove(n.id, n.order, 1)}
                  disabled={idx === list.length - 1}
                  title="Xuống"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <br />
                <span style={{ fontSize: 13, color: "#999" }}>{n.order ?? idx + 1}</span>
              </TableCell>
              <TableCell>
                {n.url ? (
                  <a href={n.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: toFullHex(n.textColor || '#ffffff') }}>
                    {n.title}
                  </a>
                ) : <span style={{ color: toFullHex(n.textColor || '#ffffff') }}>{n.title}</span>}
              </TableCell>
              <TableCell>{n.content ? n.content : "-"}</TableCell>
              <TableCell>
                {n.url
                  ? <a href={n.url} target="_blank" rel="noopener noreferrer">{n.url}</a>
                  : "-"}
              </TableCell>
              <TableCell>
                <span style={{ textTransform: "capitalize" }}>{n.color || "info"}</span>
              </TableCell>
              <TableCell>
                <span style={{ color: toFullHex(n.textColor || "#ffffff") }}>{n.textColor || "-"}</span>
              </TableCell>
              <TableCell>{n.is_active ? "✅" : "❌"}</TableCell>
              <TableCell align="center">
                <IconButton color="info" onClick={() => handleEdit(n)}>
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDelete(n.id)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {list.length === 0 &&
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ color: "#888" }}>
                Chưa có thông báo nào
              </TableCell>
            </TableRow>
          }
        </TableBody>
      </Table>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.error ? "error" : "success"}>{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}
